
import React, { useState, useContext, useEffect } from 'react'
import {
	Button,
	Card,
	CardHeader,
	CardFooter,
	Col,
	Container,
	Modal,
	ModalBody,
	ModalHeader,
	Row,
	Table,
} from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'
import { library } from "@fortawesome/fontawesome-svg-core"
import {faTag, faPenToSquare, faUser} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

library.add(faTag, faPenToSquare)

import VehiclesService from '../../../services/VehiclesService'
import { SessionContext } from '../../../contexts/SessionProvider'
import { VehiclesContext } from '../../../contexts/VehiclesProvider'
import Paginator from '../../../components/Paginator/Paginator'
import PageTitleContextMenu from '../../../components/PageTitles/PageTitleContextMenu'
import { AuctionsContext } from '../../../contexts/AuctionsProvider'
import SiteService from '../../../services/SiteService'
import VehicleInspectionsService from '../../../services/VehicleInspectionsService'
import { InspectionsContext } from '../../../contexts/InspectionsProvider'
import SelectTemplateModal from '../../../components/Modals/SelectTemplateModal'
import {subscribe, unsubscribe} from "../../../events/customEvents";
import AuctionTemplatesService from '../../../services/AuctionTemplatesService'
import AuctionsService from '../../../services/AuctionsService'
import AuctionDetail from '../../auctions/business/components/AuctionDetail'
import AuctionBids from '../../auctions/business/components/AuctionBids'
import AuctionVehicle from '../../auctions/business/components/AuctionVehicle'
import AuctionOptions from '../../auctions/business/components/AuctionOptions'
import AuctionCondition from '../../auctions/business/components/AuctionCondition'
import AuctionDamages from '../../auctions/business/components/AuctionDamages'
import AuctionPictures from '../../auctions/business/components/AuctionPictures'
import AuctionDisclosures from '../../auctions/business/components/AuctionDisclosures'

const BusinessVehiclesListing = () =>
{
	const navigate = useNavigate()
	const { t } = useTranslation()
	const vehiclesContext = useContext(VehiclesContext)
	const [isBusy, setIsBusy] = React.useState(false)
	const sessionContext = useContext(SessionContext)
	const [pagingResult, setPagingResult] = React.useState({ pageCollection: [], totalRecords: 0 })
	const auctionsContext = useContext(AuctionsContext)
	const inspectionsContext = useContext(InspectionsContext)
	const [showSelectTemplateModal, setShowSelectTemplateModal] = useState(false)
	const [selectedVehicle, setSelectedVehicle] = useState(undefined)
	const [isAuctionDetailModalOpen, setIsAuctionDetailModalOpen] = useState(false)
	const [activeDetailComponent, setActiveDetailComponent] = useState('auction-detail')
	
	useEffect(() =>
	{
		retrieveSearchResults(vehiclesContext.searchRequest)
		
		subscribe('selectTemplateModalClosed', handleSelectTemplateModalClosed)
	}, [])
	
	useEffect(() =>
	{
		return () =>
		{
			unsubscribe('selectTemplateModalClosed', handleSelectTemplateModalClosed)
		}
	}, [])
	
	const retrieveSearchResults = (searchRequest) =>
	{
		setIsBusy(true)
		VehiclesService
			.searchVehicles(searchRequest, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setPagingResult(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handlePageChange = (pageIndex) =>
	{
		vehiclesContext.setSearchRequest((previousSearchRequest) =>
		{
			let currentSearchRequest = { ...previousSearchRequest }
			currentSearchRequest.pageIndex = pageIndex
			
			retrieveSearchResults(currentSearchRequest)
			
			return currentSearchRequest
		})
	}
	
	const handleGoBack = () =>
	{
		navigate('/secure/business-vehicles-search')
	}
	
	const handleReload = () =>
	{
		retrieveSearchResults(vehiclesContext.searchRequest)
	}
	
	const selectAuctionTemplate = (vehicle) =>
	{
		setSelectedVehicle(vehicle)
		setShowSelectTemplateModal(true)
	}
	
	const handleSelectTemplateModalClosed = (event) =>
	{
		event.preventDefault()
		
		setShowSelectTemplateModal(false)
		
		if (event.detail.adminAuctionId !== undefined && event.detail.vehicle !== undefined)
		{
			setIsBusy(true)
			AuctionTemplatesService
				.createTemplateAuction(event.detail.adminAuctionId, event.detail.vehicle.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					setSelectedVehicle(undefined)
					auctionsContext.setAuction(result.data.actionResult)
					
					retrieveInspection(result.data.actionResult.auctionId)
						.then(result => {
							setActiveDetailComponent('auction-detail')
							setIsAuctionDetailModalOpen(true)
						})
						.catch(error => {
							SiteService.displayApiError(error)
						})
				})
				.catch((error) =>
				{
					setIsBusy(false)
					setSelectedVehicle(undefined)
					SiteService.displayApiError(error)
				})
		}
	}
	
	const retrieveInspection = (auctionId) =>
	{
		return new Promise((resolve, reject) =>
		{
			setIsBusy(true)
			VehicleInspectionsService
				.getVehicleInspection(auctionId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					inspectionsContext.setVehicleInspection(result.data.actionResult)
					
					resolve()
				})
				.catch((error) =>
				{
					setIsBusy(false)
					reject(error)
				})
		})
	}
	
	const closeAuctionDetailModal = () =>
	{
		setIsAuctionDetailModalOpen(false)
	}
	
	const renderAuctionDetailContent = () =>
	{
		switch (activeDetailComponent)
		{
			case 'auction-bids':
				return <AuctionBids embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-vehicle':
				return <AuctionVehicle embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-options':
				return <AuctionOptions embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-condition':
				return <AuctionCondition embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-damages':
				return <AuctionDamages embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-pictures':
				return <AuctionPictures embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-disclosures':
				return <AuctionDisclosures embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-detail':
			default:
				return <AuctionDetail embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
		}
	}
	
	const handleManageAuction = (auctionId) =>
	{
		setIsBusy(true)
		AuctionsService
			.getAuction(auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				auctionsContext.setAuction(result.data.actionResult)
				
				retrieveInspection(auctionId)
					.then(result =>
					{
						setActiveDetailComponent('auction-detail')
						setIsAuctionDetailModalOpen(true)
					})
					.catch(error =>
					{
						SiteService.displayApiError(error)
					})
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitleContextMenu
				heading={ t('Vehicles.Listing.Title') }
				subheading=""
				icon="pe-7s-car icon-gradient bg-premium-dark"
				headerOptions={ { showBack: true, backCommand: () => { handleGoBack() }, showReload: true, reloadCommand: () => { handleReload() } } }
			/>
			<Container className="mt--6" fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader></CardHeader>
							<div className="table-responsive" >
								<Table className="align-items-center table-flush">
									<thead className="thead-light">
									<tr>
										<th className="sort" data-sort="make" scope="col">{ t('Vehicles.Listing.Make') }</th>
										<th className="sort" data-sort="model" scope="col">{ t('Vehicles.Listing.Model') }</th>
										<th className="sort" data-sort="year" scope="col">{ t('Vehicles.Listing.Year') }</th>
										<th className="sort" data-sort="serialNumber" scope="col">{ t('Vehicles.Listing.Vin') }</th>
										<th className="sort" data-sort="mileage" scope="col">{ t('Vehicles.Listing.Mileage') }</th>
										<th className="sort" data-sort="turnInDate" scope="col">{ t('Vehicles.Listing.TurnInDate') }</th>
										<th className="sort" data-sort="dealerCode" scope="col">{ t('Vehicles.Listing.Dealer_Code') }</th>
										<th scope="col" />
									</tr>
									</thead>
									<tbody className="list">
									{pagingResult.pageCollection.map((pageItem) => (
										<tr key={ pageItem.vehicleId }>
											<td>{ pageItem.make }</td>
											<td>{ pageItem.model }</td>
											<td>{ pageItem.year }</td>
											<td>{ pageItem.serialNumber }</td>
											<td>{ pageItem.mileage }</td>
											<td>{ pageItem.turnInDate }</td>
											<td>{ pageItem.dealerCode }</td>
											<td className="text-right">
												<div>
													{
														(pageItem.auctionId === 0) && (
															<Button
																className="btn-icon-only rounded-circle"
																color="primary"
																title="Create Auction"
																onClick={ () => selectAuctionTemplate(pageItem) }
															>
																<span className="btn-inner--icon">
																	<FontAwesomeIcon icon={ faTag } />
																</span>
															</Button>
														)
													}
													{
														(pageItem.auctionId > 0) && (
															<Button
																className="btn-icon-only rounded-circle"
																color="secondary"
																title="Manage Auction"
																onClick={ () => handleManageAuction(pageItem.auctionId) }
															>
																<span className="btn-inner--icon">
																	<FontAwesomeIcon icon={ faPenToSquare } />
																</span>
															</Button>
														)
													}
												</div>
											</td>
										</tr>
									))}
									</tbody>
								</Table>
							</div>
							<CardFooter className="py-4">
								{
									(pagingResult.pageCollection.length === 0) && (
										<Row style={{ marginBottom: '10px' }}>
											<Col md="12" style={{ textAlign: 'center' }}>
												<h5 className="mb-0">{t('Vehicles.Listing.No_Results')}</h5>
											</Col>
										</Row>
									)
								}
								<div style={{ marginLeft: 'auto' }}>
									{
										(pagingResult.pageCollection.length > 0) && (
											<nav>
												<Paginator
													currentPage={ vehiclesContext.searchRequest.pageIndex }
													pages={parseInt(((pagingResult.totalRecords - 1) / vehiclesContext.searchRequest.pageSize) + 1, 10)}
													selectPage={ handlePageChange }
												/>
											</nav>
										)
									}
								</div>
							</CardFooter>
						</Card>
					</div>
				</Row>
			</Container>
			
			<Modal
				isOpen={ isAuctionDetailModalOpen }
				toggle={ closeAuctionDetailModal }
				size="xl"
				backdrop="static"
				fade={ false }
				style={{ maxWidth: '95vw' }}
			>
				<ModalHeader toggle={ closeAuctionDetailModal }>
					Auction Details
				</ModalHeader>
				<ModalBody>
					<div>
						{ renderAuctionDetailContent() }
					</div>
				</ModalBody>
			</Modal>
			
			<SelectTemplateModal showSelectTemplateModal={ showSelectTemplateModal } selectedVehicle={ selectedVehicle } />
		</>
	)
}

export default BusinessVehiclesListing
